import type { ActivityRow, Sport } from "@/lib/api/types";
import { PB_DISTANCES, type PbDistance } from "./distances";

export type PbEffort = { time: number; ignored: boolean; rounded: boolean };

/** Normalised activity used everywhere in the UI. */
export type Activity = Omit<ActivityRow, "distance" | "sport"> & {
  distance: number; // metres, numeric
  sport: Sport; // lower-cased
  startDate: Date; // local start time (server strings treated as local wall time)
  startIso: string; // ISO-like local string used for lexicographic range compares
  paceSecPerKm: number | null;
  pbs: Partial<Record<number, PbEffort>>; // keyed by PbDistance.key
};

export const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Server timestamps have no timezone; treat them as local wall-clock time (matches the Quasar app). */
export function parseLocal(value: string | Date | null | undefined): Date {
  if (!value) return new Date(NaN);
  // Dates never reach here over the wire, but the server port of this module
  // is fed pg Date objects — keep the two copies identical (see #247).
  if (value instanceof Date) return value;
  const s = String(value).replace("Z", "").replace(" ", "T");
  return new Date(s);
}

export function localIso(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/**
 * Port of AppStore.processPbRoundup: when the user opts into "round up" and an
 * activity falls short of a PB distance by at most `roundUpMetres`, pro-rate
 * the elapsed time so it counts for that distance.
 */
export function applyRoundUp(row: ActivityRow, roundUpMetres: number): ActivityRow {
  if (!roundUpMetres || roundUpMetres <= 0) return row;
  const distance = num(row.distance);
  const elapsed = num(row.elapsed_time);
  if (!distance || !elapsed) return row;
  const out: ActivityRow = { ...row };
  for (const d of PB_DISTANCES) {
    if (distance < d.metres && d.metres <= distance + roundUpMetres && !num(out[d.field])) {
      (out as Record<string, unknown>)[d.field] = Math.round((elapsed / distance) * d.metres);
      (out as Record<string, unknown>)[`${d.field}_rounded`] = true;
    }
  }
  return out;
}

export function normaliseActivity(row: ActivityRow): Activity {
  const distance = num(row.distance) ?? 0;
  const moving = num(row.moving_time);
  const startDate = parseLocal(row.start_time ?? row.local_start_time ?? row.activity_date);
  const pbs: Activity["pbs"] = {};
  for (const d of PB_DISTANCES) {
    const t = num(row[d.field]);
    if (t && t > 0) {
      pbs[d.key] = {
        time: t,
        ignored: !!row[d.ignoreField],
        rounded: !!(row as Record<string, unknown>)[`${d.field}_rounded`],
      };
    }
  }
  return {
    ...row,
    distance,
    sport: String(row.sport ?? "").toLowerCase(),
    startDate,
    startIso: Number.isNaN(startDate.getTime()) ? "" : localIso(startDate),
    paceSecPerKm: distance > 0 && moving ? moving / (distance / 1000) : null,
    pbs,
    tags: Array.isArray(row.tags) ? row.tags : [],
  };
}

export function normaliseActivities(rows: ActivityRow[], roundUpMetres = 0): Activity[] {
  return rows.map((r) => normaliseActivity(applyRoundUp(r, roundUpMetres)));
}

export const effortFor = (a: Activity, d: PbDistance): PbEffort | undefined => a.pbs[d.key];
export const isRun = (a: Activity) => a.sport === "running";

export function latestActivity(list: Activity[]): Activity | undefined {
  return list.reduce<Activity | undefined>((best, a) => (!best || a.startDate > best.startDate ? a : best), undefined);
}
export function oldestActivity(list: Activity[]): Activity | undefined {
  return list
    .filter((a) => a.startDate.getFullYear() > 1975)
    .reduce<Activity | undefined>((best, a) => (!best || a.startDate < best.startDate ? a : best), undefined);
}
export function yearsActive(list: Activity[]): number[] {
  const ys = new Set<number>();
  for (const a of list) {
    const y = a.startDate.getFullYear();
    if (y > 1975) ys.add(y);
  }
  return [...ys].sort((a, b) => b - a);
}
